import React, { useEffect } from "react";
import DupontDialog from "../components/DupontDialog";
import InputBox from "../components/InputBox";
import DupontDatePicker from "../components/DatePopover/DupontDatePicker";
import { useTranslation } from "react-i18next";
import { DatePickerConfig } from "../components/DatePopover/DateTimeConfig";
import { APPINSIGHTS_STRINGS } from "../constants/appInsightsStrings";
import { trackEvent } from "../utils/appInsights";
import { useState } from "react";
import { editCip, getCIPs, uploadCips } from "../network/cipAPI";
import moment from "moment";
import { TextFieldValidator } from "../components/InputBox/TextFieldValidator";
import { useDispatch, useSelector } from "react-redux";
import { storeCleanData } from "../Store/TemplateStore/template.action";
import { getSortedData } from "../utils/tableDataFormatter";
import PopoverText from "../components/PopoverText";
import InfoIconGreen from "../assets/info-icon-green.svg";
import {
  ADD_CIP_INFORMATION,
  PLANT_DETAILS,
} from "./defaultCIPInformation/defaultCIPInformationFields.config";
import { ValueStrings } from "../utils/constantsString";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../components/Notifications";

const AddCIPDialog = ({
  open,
  setOpen,
  rowData,
  setRowData,
  isEdited,
  editedRow,
  gridAPI,
  updateStoreCips,
  isUSerAdded,
  onClose,
  fileInfo,
  max_date,
  min_date,
  setView,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState(
    editedRow && editedRow.type ? editedRow.type : "cip"
  );
  const acid_soak_duration = useSelector((state) => state.acid_soak_duration);

  const acid_used = useSelector((state) => state.acid_used);
  const acid_ph = useSelector((state) => state.acid_ph);
  const acid_temp = useSelector((state) => state.acid_temp);
  const alkali_soak_duration = useSelector(
    (state) => state.alkali_soak_duration
  );
  const alkali_used = useSelector((state) => state.alkali_used);
  const alkali_ph = useSelector((state) => state.alkali_ph);
  const alkali_temp = useSelector((state) => state.alkali_temp);
  const add_info = useSelector((state) => state.add_info);
  const temperature = useSelector((state) => state.temperature);
  const mass = useSelector((state) => state.mass);
  const minTemp = temperature === "C" ? 0 : 32;
  const maxTemp = temperature === "C" ? 40 : 104;
  const minAcidUsed = mass === "kg" ? 0 : 0;
  const maxAcidUsed = mass === "kg" ? 100 : 220;

  const defaultFields = ADD_CIP_INFORMATION(
    type,
    minTemp,
    maxTemp,
    minAcidUsed,
    maxAcidUsed
  );

  const [cipStartDate, setStartDate] = useState(
    editedRow ? new Date(editedRow.start_date) : null
  );
  const [cipEndDate, setEndDate] = useState(
    editedRow && editedRow.end_date ? new Date(editedRow.end_date) : null
  );

  const plantDetails = PLANT_DETAILS(t);
  const setCipModalInitialValues = (type) => {
    const initialValues = {
      type: type,
      acid_used: acid_used,
      alkali_used: alkali_used,
      acid_ph: acid_ph,
      alkali_ph: alkali_ph,
      acid_temp: acid_temp,
      alkali_temp: alkali_temp,
      acid_soak_duration: acid_soak_duration,
      alkali_soak_duration: alkali_soak_duration,
      add_info: add_info,
    };
    return initialValues;
  };

  const [errors, setErrors] = useState({
    acid_soak_duration: null,
    acid_used: null,
    acid_ph: null,
    acid_temp: null,
    alkali_soak_duration: null,
    alkali_used: null,
    alkali_ph: null,
    alkali_temp: null,
  });

  const [cipModal, setCipModal] = useState(
    editedRow ? editedRow : setCipModalInitialValues(type)
  );

  useEffect(() => {
    const acid_soak_duration_value = type === "cip" ? acid_soak_duration : 10;
    const alkali_soak_duration_value =
      type === "cip" ? alkali_soak_duration : 10;
    const alkali_temp_value = temperature === "C" ? alkali_temp : 77;
    const acid_temp_value = temperature === "C" ? acid_temp : 77;
    const alkali_used_value = mass === "kg" ? alkali_used : 0.22;
    const acid_used_value = mass === "kg" ? acid_used : 0.22;

    setErrors({
      acid_soak_duration: null,
      acid_used: null,
      acid_ph: null,
      acid_temp: null,
      alkali_soak_duration: null,
      alkali_used: null,
      alkali_ph: null,
      alkali_temp: null,
    });

    setCipModal((prevState) => ({
      ...prevState,
      type: type,
      acid_soak_duration:
        prevState.acid_soak_duration ?? acid_soak_duration_value,
      alkali_soak_duration:
        prevState.alkali_soak_duration ?? alkali_soak_duration_value,
      alkali_temp: prevState.alkali_temp ?? alkali_temp_value,
      acid_temp: prevState.acid_temp ?? acid_temp_value,
      acid_used: prevState.acid_used ?? acid_used_value,
      alkali_used: prevState.alkali_used ?? alkali_used_value,
      acid_ph: prevState.acid_ph ?? acid_ph,
      alkali_ph: prevState.alkali_ph ?? alkali_ph,
    }));
  }, [type]);

  const [isLoading, setIsLoading] = useState(false);
  const [minDateLimit, setMinDateLimit] = useState(null);
  const [maxDateLimit, setMaxDateLimit] = useState(null);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  useEffect(() => {
    const fetchLimit = async () => {
      try {
        setMinDateLimit(moment(min_date || fileInfo.min_date).toDate());
        setMaxDateLimit(
          moment(max_date || fileInfo.max_date)
            .add(1, "days")
            .toDate()
        );
      } catch (err) {}
    };
    fetchLimit();
  }, []);
  const showErrorNotification = (detail) => {
    if (detail) {
      createNotification(
        ERROR_NOTIFICATION,
        detail.error || t("notificationStrings.somethingWrong"),
        ""
      );
      setIsLoading(false);
      return true;
    }
    return false;
  };

  const onAddClicked = async (data) => {
    if (data !== "CONFIRM") {
      setOpen(false);
      if (typeof onClose === "function") onClose();
      return;
    }

    setIsLoading(true);

    try {
      let response;
      if (!isEdited) {
        response = await uploadCips(cipModal);
      } else {
        response = await editCip(cipModal.cip_id, cipModal);
      }

      if (showErrorNotification(response?.detail)) return;

      const cips = await getCIPs();
      if (cips?.status !== 200 || !cips?.data) return;

      const filteredCips = isUSerAdded
        ? cips.data.filter((item) => !item.is_tool_identified)
        : cips.data;

      // Track the CIP add/edit event
      trackEvent(
        isEdited ? APPINSIGHTS_STRINGS.CIP_EDITED : APPINSIGHTS_STRINGS.CIP_ADDED,
        {
          type: cipModal.type,
          startDate: cipModal.start_date,
          endDate: cipModal.end_date,
          acidSoakDuration: cipModal.acid_soak_duration,
          alkaliSoakDuration: cipModal.alkali_soak_duration,
          hasAdditionalInfo: !!cipModal.add_info
        }
      );

      setRowData(getSortedData(filteredCips));
      setView(2);

      if (isEdited && gridAPI) {
        gridAPI.setRowData(filteredCips);
      }

      updateStoreCips?.(cips.data);
      if (typeof onSaved === "function") {
        onSaved(cipModal);
      }
    } catch (err) {
      console.error("Error:", err);
      createNotification(
        ERROR_NOTIFICATION,
        err?.message || t("notificationStrings.somethingWrong"),
        ""
      );
    } finally {
      setIsLoading(false);
      setOpen(false);
      if (typeof onClose === "function") onClose();
    }
  };

  const onStartDateChange = (value) => {
    setStartDate(value);
    setCipModal({
      ...cipModal,
      start_date: moment(value).format("YYYY-MM-DD HH:mm:ss"),
    });
    if (cipEndDate && moment(value).isSameOrAfter(cipEndDate)) {
      setStartDateError(t("addCIPStrings.startDateError"));
      setEndDateError("");
    } else {
      setStartDateError("");
      setEndDateError("");
    }
  };

  const onEndDateChange = (value) => {
    if (value === null) {
      setEndDate("");
      setCipModal({ ...cipModal, end_date: "" });
      setEndDateError("");
      setStartDateError("");
    } else {
      setEndDate(value);
      setCipModal({
        ...cipModal,
        end_date: moment(value).format("YYYY-MM-DD HH:mm:ss"),
      });
      if (
        cipStartDate &&
        moment(value).isSame(cipStartDate, "day") &&
        moment(value).isSameOrBefore(cipStartDate)
      ) {
        setEndDateError(t("addCIPStrings.startDateError"));
        setStartDateError("");
      } else {
        setEndDateError("");
        setStartDateError("");
      }
    }
  };

  const handleRadioChange = (event) => {
    const { name } = event.target;
    setCipModal({ ...cipModal, type: name });
    setType(name);
  };

  const handleInputAddChange = (key, value) => {
    setCipModal({ ...cipModal, [key]: value });
  };

  const handleInputChange = (field, value, minValue, maxValue) => {
    setErrors({ ...errors, [field]: null });
    setCipModal({ ...cipModal, [field]: value || null });

    if ((value === "" && minValue !== null) || parseFloat(value) < minValue) {
      setErrors({
        ...errors,
        [field]: `${t("defaultInformationStrings.cannotLess")} ${minValue}`,
      });
    } else if (
      maxValue !== null &&
      (parseFloat(value) < minValue || parseFloat(value) > maxValue)
    ) {
      setErrors({
        ...errors,
        [field]: `${t("defaultInformationStrings.cannotLess")} ${minValue} ${t(
          "defaultInformationStrings.greaterThan"
        )} ${maxValue}`,
      });
    }
  };

  const getText = () =>
    isEdited ? t("ValueStrings.edit") : t("ValueStrings.add");
  const noError = Object.values(errors).every(
    (error) => error === "" || error === null
  );

  const replaceUnit = (value) => {
    if (
      value.includes(t("defaultInformationStrings.acid_used")) ||
      value.includes(t("defaultInformationStrings.alkali_used"))
    ) {
      value = value.replace("<unit>", mass);
    } else if (
      value.includes(t("defaultInformationStrings.acid_soak_duration")) ||
      value.includes(t("defaultInformationStrings.alkali_soak_duration"))
    ) {
      if (type === "cip") {
        value = value.replace("<unit>", "hours");
      } else if (type === "ceb") {
        value = value.replace("<unit>", "minutes");
      }
    } else {
      value = value.replace("<unit>", `\u00B0${temperature}`);
    }
    if (type === "cip") {
      value = value.replace("(<CEC>)", "CIP");
    } else if (type === "ceb") {
      value = value.replace("(<CEC>)", "CEB");
    } else if (type === "shutdown") {
      value = value.replace("(<CEC>)", "");
    }
    return value;
  };
  const replaceText = (value) => {
    if (type === "cip") {
      value = value.replace("(<CEC>)", "CIP");
    } else if (type === "ceb") {
      value = value.replace("(<CEC>)", "CEB");
    } else if (type === "shutdown") {
      value = value.replace("(<CEC>)", "Shutdown");
    }
    return value;
  };

  // Utility function to compare two objects shallowly
  const areObjectsShallowEqual = (a, b) => {
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (let key of aKeys) {
      if (a[key] !== b[key]) return false;
    }
    return true;
  };

  return (
    <DupontDialog
      title={
        isEdited
          ? t("CIPShutdownStrings.editCip")
          : t("CIPShutdownStrings.addCip")
      }
      subTitle={t("CIPShutdownStrings.enterDetails").replace(
        "<type>",
        getText()
      )}
      confirmBtnText={isEdited ? t("ValueStrings.save") : t("ValueStrings.add")}
      cancelBtnText={t("ValueStrings.cancel")}
      open
      onClose={onAddClicked}
      confirmDisabled={
        !cipStartDate ||
        startDateError ||
        !noError ||
        !cipEndDate ||
        endDateError ||
        (isEdited && areObjectsShallowEqual(cipModal, editedRow)) // Disable if no changes in edit mode
      }
      isOverlayLoader={isLoading}
      customHeight={"525px"}
      maxWidth={"800px"}
    >
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "50px",
          zIndex: 1000,
        }}
      >
        <PopoverText
          descriptor={<img src={InfoIconGreen} alt="icon" />}
          descriptionText={t(
            "CIPShutdownStrings.recordPastCIPsAndShutdownsWithNotes"
          )}
        />
      </div>
      <div
        className={type === "shutdown" ? "add-shutdown-popup" : "add-cip-popup"}
      >
        <div
          className={
            type === "shutdown" ? "add-shutdown-popup" : "add-cip-popup"
          }
        >
          <div className="grid-container marginBtm10 ">
            <div className="grid-item">
              <DupontDatePicker
                selected={cipStartDate}
                onChange={onStartDateChange}
                startDate={new Date()}
                showTimeSelect={true}
                dateFormat={ValueStrings.DATE_TIME_SECONDS_FORMAT}
                placeholderText={
                  ValueStrings.DATE_TIME_SECONDS_FORMAT_PLACEHOLDER
                }
                value={cipStartDate}
                minDate={minDateLimit}
                minTime={
                  cipStartDate &&
                  minDateLimit &&
                  moment(cipStartDate).isSame(minDateLimit, "day")
                    ? minDateLimit
                    : new Date(0, 0, 0, 0, 0, 0)
                }
                maxDate={maxDateLimit}
                maxTime={
                  cipStartDate &&
                  maxDateLimit &&
                  moment(cipStartDate).isSame(maxDateLimit, "day")
                    ? maxDateLimit
                    : new Date(0, 0, 0, 23, 59, 59)
                }
                label={t("addCIPStrings.startDate")}
                labelClass="dupont-form-label wrapText text-left"
                className={"text-left width-fill"}
                error={startDateError}
                {...DatePickerConfig}
              />
            </div>
            <div className="grid-item">
              <DupontDatePicker
                selected={cipEndDate}
                onChange={onEndDateChange}
                startDate={new Date()}
                showTimeSelect={true}
                dateFormat={ValueStrings.DATE_TIME_SECONDS_FORMAT}
                placeholderText={
                  ValueStrings.DATE_TIME_SECONDS_FORMAT_PLACEHOLDER
                }
                value={cipEndDate}
                label={t("addCIPStrings.endDate")}
                disabled={!Boolean(cipStartDate)}
                minDate={cipStartDate}
                minTime={
                  cipStartDate &&
                  cipEndDate &&
                  moment(cipEndDate).isSame(cipStartDate, "day")
                    ? cipStartDate
                    : new Date(0, 0, 0, 0, 0, 0)
                }
                maxDate={maxDateLimit}
                maxTime={
                  cipEndDate &&
                  maxDateLimit &&
                  moment(cipEndDate).isSame(maxDateLimit, "day")
                    ? maxDateLimit
                    : new Date(0, 0, 0, 23, 59, 59)
                }
                error={endDateError}
                labelClass="dupont-form-label wrapText text-left"
                className={"width-fill"}
                {...DatePickerConfig}
              />
            </div>
            <div className="grid-item">
              <div style={{ color: "black", fontSize: "16px" }}>
                {t("addCIPStrings.type")}
              </div>
              <div
                onChange={handleRadioChange}
                style={{
                  marginTop: "15px",
                  justifySelf: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <label className=" radiocontainer optionalDescriptionText">
                  {t("addCIPStrings.ceb")}
                  <input
                    type="radio"
                    name="ceb"
                    value={false}
                    checked={type === "ceb"}
                  />
                  <span className="radioButton"> </span>
                </label>
                <label className="radiocontainer optionalDescriptionText">
                  {t("addCIPStrings.cip")}
                  <input
                    className="radioLabel"
                    type="radio"
                    value={true}
                    name="cip"
                    checked={type === "cip"}
                  />
                  <span className="radioButton"> </span>
                </label>
                <label className=" radiocontainer optionalDescriptionText">
                  {t("addCIPStrings.shutDown")}
                  <input
                    type="radio"
                    name="shutdown"
                    value={false}
                    checked={type === "shutdown"}
                  />
                  <span className="radioButton"> </span>
                </label>
              </div>
            </div>
          </div>
          {(type === "ceb" || type === "cip") && (
            <div className="sub-grid-container ">
              {Object.keys(defaultFields).map((item) => (
                <div key={item} className="sub-section-container">
                  <div className="add-cip-grid-container ">
                    {Object.values(defaultFields[item]).map((field, index) => (
                      <div className="sub-grid-item " key={index}>
                        <InputBox
                          value={cipModal[field.key]}
                          name={field.key}
                          labelText={replaceUnit(
                            t(`defaultInformationStrings.${field.key}`)
                          )}
                          onChange={(value) =>
                            handleInputChange(
                              field.key,
                              value,
                              field.minValue,
                              field.maxValue
                            )
                          }
                          validator={TextFieldValidator.NUMBER_WITH_DECIMAL}
                          errorMsg={errors[field.key]}
                          maxLength={10}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {Object.keys(plantDetails).map((item) => (
            <div key={item} className=" marginTop10">
              {Object.values(plantDetails[item]).map((field, index) => (
                <div className="sub-grid-item " key={index}>
                  <InputBox
                    value={cipModal[field.key]}
                    labelText={replaceText(
                      t(`defaultInformationStrings.add_info`)
                    )}
                    rows={field.multiline}
                    onChange={(value) => handleInputAddChange(field.key, value)}
                    validator={
                      TextFieldValidator.ALPHANUMERIC_WITH_CHINESE_AND_SPECIAL
                    }
                    maxLength={field.maxChar}
                    placeholder={t(`addCIPStrings.defaultText`)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </DupontDialog>
  );
};

export default AddCIPDialog;
